/**
 * Helpers for attaching a book version as chat reference context.
 */

export type CloudVersionSummary = {
  id: string;
  name: string;
  status: string;
  frozenAt?: string | null;
};

export type AttachedChatVersion = {
  id: string;
  name: string;
  status: string;
};

export type AttachedVersionCatalogChapter = {
  title: string;
  order: number;
  kind?: string;
  chapterNumber?: number | null;
  wordCount?: number;
};

const EXCERPT_CHARS = 400;

export function versionsFromBookMetadata(
  metadata: Record<string, unknown> | null | undefined
): CloudVersionSummary[] {
  const raw = metadata?.cloudVersions;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((v): v is Record<string, unknown> => typeof v === "object" && v !== null)
    .map((v) => ({
      id: typeof v.id === "string" ? v.id : "",
      name: typeof v.name === "string" ? v.name : "",
      status: typeof v.status === "string" ? v.status : "",
      frozenAt: typeof v.frozenAt === "string" ? v.frozenAt : null,
    }))
    .filter((v) => v.id);
}

export function currentVersionIdFromMetadata(
  metadata: Record<string, unknown> | null | undefined
): string | null {
  const id = metadata?.currentVersionId;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}

export function displayVersionLabel(v: { name: string; status: string }): string {
  const name = (v.name || "").trim() || "Untitled version";
  return v.status ? `${name} (${v.status})` : name;
}

/** Short plain-text excerpt for chat context. */
export function excerptForChat(text: string, max = EXCERPT_CHARS): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max).trim()}…`;
}

export function formatAttachedVersionContext(params: {
  version: AttachedChatVersion;
  isOpenManuscript: boolean;
  chapters: AttachedVersionCatalogChapter[];
  /** Optional excerpts keyed by chapter order (open manuscript only). */
  excerptsByOrder?: Record<number, string>;
}): string {
  const lines: string[] = [
    `## Attached book version (reference only — do not treat as the editable manuscript unless the user says so)`,
    `Version: "${displayVersionLabel(params.version)}" (id=${params.version.id})`,
    params.isOpenManuscript
      ? "This is the version currently open in the editor."
      : "This is a different version than the open manuscript. Use it for comparison/reference.",
    `Spine (${params.chapters.length} items):`,
  ];

  for (const ch of params.chapters) {
    const kind = ch.kind && ch.kind !== "chapter" ? ` [${ch.kind}]` : "";
    const num =
      ch.kind === "chapter" || !ch.kind
        ? ch.chapterNumber != null
          ? `Ch ${ch.chapterNumber}`
          : `#${ch.order}`
        : `#${ch.order}`;
    const words = typeof ch.wordCount === "number" ? ` · ${ch.wordCount} words` : "";
    lines.push(`- ${num}${kind}: ${ch.title || "Untitled"}${words}`);
    const excerpt = params.excerptsByOrder?.[ch.order];
    if (excerpt) lines.push(`  Excerpt: ${excerpt}`);
  }

  return lines.join("\n");
}
